import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import styles from "./GameRelatedListSlider.module.scss";
import TopSwiperItem from "../../common/top/TopSwiperItem";
import ProjectListItem from "../project-list/ProjectListItem";
import EventSwiperItem from "../top/event/EventSwiperItem";
import TopicsListItem from "../top/topics/TopicsListItem";
import { SonicDBEntryData } from "../../common/classes/SonicDBEntry";
import { SonicProjectData } from "../../common/classes/SonicProject";
import { SonicEventData } from "../../common/classes/SonicEvent";
import { SonicTopicsData } from "../../common/classes/SonicTopics";
import { timestampToTime, getLargestImage } from "../../utils/GeneralUtils";
import { SonicTeamData } from "../../common/classes/SonicTeam";
import LoadingSpinner from "../../common/LoadingSpinner";
import SeeMoreLink from "./SeeMoreLink";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SonicLink from "../../common/routing/SonicLink";

type Props = {
  isLoading: boolean;
  gameTitle?: string;
  profiles: Array<SonicDBEntryData>;
  teams: Array<SonicTeamData>;
  projects: Array<SonicProjectData>;
  events: Array<SonicEventData>;
  topics: Array<SonicTopicsData>;
};
const GameRelatedListSlider: React.FC<Props> = ({
  isLoading,
  gameTitle,
  profiles,
  teams,
  projects,
  events,
  topics,
}) => {
  return (
    <div>
      {!isLoading && profiles.length === 0 ? null : (
        <section className={styles["c-game-slider"]}>
          <div className={styles["c-game-slider__head"]}>
            <h2 className={styles["c-game-slider__head__title"]}>
              プロフィール
            </h2>
            <SonicLink
              to={`/person/?games=${gameTitle}`}
              className={styles["c-game-slider__head__link"]}
            >
              もっとみる
            </SonicLink>
          </div>
          <div className={styles["c-game-slider-container"]}>
            <Swiper
              modules={[Navigation]}
              className={styles["c-game-slider-container-swiper"]}
              slidesPerView={"auto"}
              freeMode={true}
              roundLengths={true}
              navigation={{
                prevEl: `.${styles["profile-prev"]}`,
                nextEl: `.${styles["profile-next"]}`,
                disabledClass: `${styles["disabled"]}`,
              }}
            >
              {isLoading ? (
                <LoadingSpinner space={1} />
              ) : (
                <>
                  {profiles.map((p, i) => (
                    <SwiperSlide
                      key={i}
                      className={styles["c-game-slider-container-swiper__item"]}
                    >
                      <TopSwiperItem
                        id={p.id}
                        name={p.name}
                        img_src={p.imgs?.[0]?.medium?.url}
                        logo_src={p.team?.[0]?.img?.medium?.url}
                        team_color={p.team?.[0]?.color}
                        isFeatured={false}
                        isTeam={false}
                        link_path={`/person/${p.id}`}
                        verified={p.verified}
                      />
                    </SwiperSlide>
                  ))}
                  <SwiperSlide
                    key={"profile-more"}
                    className={styles["c-game-slider-container-swiper__item"]}
                  >
                    <SeeMoreLink link={`/person/?games=${gameTitle}`} />
                  </SwiperSlide>
                </>
              )}
            </Swiper>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["profile-prev"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-left"]} />
            </div>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["profile-next"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-right"]} />
            </div>
          </div>
        </section>
      )}

      {!isLoading && teams.length === 0 ? null : (
        <section className={styles["c-game-slider"]}>
          <div className={styles["c-game-slider__head"]}>
            <h2 className={styles["c-game-slider__head__title"]}>チーム</h2>
            <SonicLink
              to={`/team/?games=${gameTitle}`}
              className={styles["c-game-slider__head__link"]}
            >
              もっとみる
            </SonicLink>
          </div>
          <div className={styles["c-game-slider-container"]}>
            <Swiper
              modules={[Navigation]}
              className={styles["c-game-slider-container-swiper"]}
              slidesPerView={"auto"}
              freeMode={true}
              roundLengths={true}
              navigation={{
                prevEl: `.${styles["team-prev"]}`,
                nextEl: `.${styles["team-next"]}`,
                disabledClass: `${styles["disabled"]}`,
              }}
            >
              {isLoading ? (
                <LoadingSpinner space={1} />
              ) : (
                <>
                  {teams.map((t, i) => (
                    <SwiperSlide
                      key={i}
                      className={styles["c-game-slider-container-swiper__item"]}
                    >
                      <TopSwiperItem
                        id={t.id}
                        name={t.name}
                        img_src={t.term?.img?.medium?.url}
                        team_color={t.term?.color}
                        isFeatured={false}
                        isTeam={true}
                        link_path={`/team/${t.id}`}
                        verified={false}
                      />
                    </SwiperSlide>
                  ))}
                  <SwiperSlide
                    key={"team-more"}
                    className={styles["c-game-slider-container-swiper__item"]}
                  >
                    <SeeMoreLink link={`/team/?games=${gameTitle}`} />
                  </SwiperSlide>
                </>
              )}
            </Swiper>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["team-prev"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-left"]} />
            </div>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["team-next"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-right"]} />
            </div>
          </div>
        </section>
      )}

      {!isLoading && projects.length === 0 ? null : (
        <section className={styles["c-game-slider"]}>
          <div className={styles["c-game-slider__head"]}>
            <h2 className={styles["c-game-slider__head__title"]}>
              プロジェクト
            </h2>
            <SonicLink
              to={`/project/list/?games=${gameTitle}`}
              className={styles["c-game-slider__head__link"]}
            >
              もっとみる
            </SonicLink>
          </div>
          <div className={styles["c-game-slider-container"]}>
            <Swiper
              modules={[Navigation]}
              className={styles["c-game-slider-container-swiper"]}
              slidesPerView={"auto"}
              freeMode={true}
              roundLengths={true}
              navigation={{
                prevEl: `.${styles["project-prev"]}`,
                nextEl: `.${styles["project-next"]}`,
                disabledClass: `${styles["disabled"]}`,
              }}
            >
              {isLoading ? (
                <LoadingSpinner space={1} />
              ) : (
                <>
                  {projects.map((p, i) => (
                    <SwiperSlide
                      key={i}
                      className={`${styles["c-game-slider-container-swiper__item"]} ${styles["project"]}`}
                    >
                      <ProjectListItem
                        id={p.id}
                        name={p.name}
                        type={p.type}
                        imgs={p.imgs}
                        applicants={p.applicants}
                        end_date={p.end_date}
                        owner={p.owner_name}
                        isJikei={
                          p.role_restriction.includes("jikei_student")
                            ? true
                            : false
                        }
                        isGridLayout={false}
                      />
                    </SwiperSlide>
                  ))}
                  <SwiperSlide
                    key={"project-more"}
                    className={`${styles["c-game-slider-container-swiper__item"]} ${styles["project"]}`}
                  >
                    <SeeMoreLink link={`/project/list/?games=${gameTitle}`} />
                  </SwiperSlide>
                </>
              )}
            </Swiper>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["project-prev"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-left"]} />
            </div>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["project-next"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-right"]} />
            </div>
          </div>
        </section>
      )}

      {!isLoading && events.length === 0 ? null : (
        <section className={styles["c-game-slider"]}>
          <div className={styles["c-game-slider__head"]}>
            <h2 className={styles["c-game-slider__head__title"]}>イベント</h2>
            <SonicLink
              to={`/event/?games=${gameTitle}`}
              className={styles["c-game-slider__head__link"]}
            >
              もっとみる
            </SonicLink>
          </div>
          <div className={styles["c-game-slider-container"]}>
            <Swiper
              modules={[Navigation]}
              className={styles["c-game-slider-container-swiper"]}
              slidesPerView={"auto"}
              freeMode={true}
              roundLengths={true}
              navigation={{
                prevEl: `.${styles["event-prev"]}`,
                nextEl: `.${styles["event-next"]}`,
                disabledClass: `${styles["disabled"]}`,
              }}
            >
              {isLoading ? (
                <LoadingSpinner space={1} />
              ) : (
                <>
                  {events.map((e, i) => (
                    <SwiperSlide
                      key={i}
                      className={`${styles["c-game-slider-container-swiper__item"]} ${styles["event"]}`}
                    >
                      <EventSwiperItem
                        id={e.id}
                        img={e.imgs?.[0]?.large?.url}
                        title={e.name}
                        type={e.event_type}
                        isJikei={
                          e.role_restriction.includes("jikei_student")
                            ? true
                            : false
                        }
                      />
                    </SwiperSlide>
                  ))}
                  <SwiperSlide
                    key={"event-more"}
                    className={`${styles["c-game-slider-container-swiper__item"]} ${styles["event"]}`}
                  >
                    <SeeMoreLink link={`/event/?games=${gameTitle}`} />
                  </SwiperSlide>
                </>
              )}
            </Swiper>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["event-prev"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-left"]} />
            </div>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["event-next"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-right"]} />
            </div>
          </div>
        </section>
      )}

      {!isLoading && topics.length === 0 ? null : (
        <section className={styles["c-game-slider"]}>
          <div className={styles["c-game-slider__head"]}>
            <h2 className={styles["c-game-slider__head__title"]}>トピックス</h2>
            <SonicLink
              to={`/topics/?games=${gameTitle}`}
              className={styles["c-game-slider__head__link"]}
            >
              もっとみる
            </SonicLink>
          </div>
          <div className={styles["c-game-slider-container"]}>
            <Swiper
              modules={[Navigation]}
              className={styles["c-game-slider-container-swiper"]}
              slidesPerView={"auto"}
              freeMode={true}
              roundLengths={true}
              navigation={{
                prevEl: `.${styles["topics-prev"]}`,
                nextEl: `.${styles["topics-next"]}`,
                disabledClass: `${styles["disabled"]}`,
              }}
            >
              {isLoading ? (
                <LoadingSpinner space={1} />
              ) : (
                <>
                  {topics.map((t, i) => (
                    <SwiperSlide
                      key={i}
                      className={`${styles["c-game-slider-container-swiper__item"]} ${styles["topics"]}`}
                    >
                      <TopicsListItem
                        to={t.id}
                        category={t.topic_type.name}
                        date={timestampToTime(t.start_date, false, false, ".")}
                        title={t.name}
                        author={t.author_name}
                        img_src={getLargestImage(t.imgs?.[0])}
                        tags={t.tags}
                      />
                    </SwiperSlide>
                  ))}
                  <SwiperSlide
                    key={"topics-more"}
                    className={`${styles["c-game-slider-container-swiper__item"]} ${styles["topics"]}`}
                  >
                    <SeeMoreLink link={`/topics/?games=${gameTitle}`} />
                  </SwiperSlide>
                </>
              )}
            </Swiper>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["topics-prev"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-left"]} />
            </div>
            <div
              className={`${styles["c-game-slider-container-btn"]} ${styles["topics-next"]}`}
            >
              <FontAwesomeIcon icon={["fal", "arrow-right"]} />
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default GameRelatedListSlider;
